/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mySubscriptionService } from '@/services/billing/my-subscription.service';
import {
  SubscriptionSummary,
  InitiatePaymentResponse,
  ModuleSubscriptionItem,
  CreateSubscriptionRequest,
  AddModuleRequest,
  ChangeTierRequest,
} from '@/types/superadmin.types';
import { ApiError } from '@/services/api/types';
import { useAuthStore } from '@/stores/authStore';
import { invoiceKeys } from './useInvoices';
import { myModulesKeys } from './useModuleFeatures';
import { menuKeys } from '@/modules/venta/hooks/useMenus';

export const mySubscriptionKeys = {
  all: ['my-subscription'] as const,
  summary: () => [...mySubscriptionKeys.all, 'summary'] as const,
};

export function useMySubscription() {
  const { user } = useAuthStore();
  const isOrgUser = !!user && user.role !== 'SUPER_ADMIN' && !!user.orgId;

  return useQuery<SubscriptionSummary, ApiError>({
    queryKey: mySubscriptionKeys.summary(),
    queryFn: mySubscriptionService.getMySubscription,
    enabled: isOrgUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
}

export function useMySubscriptionReactivate() {
  const queryClient = useQueryClient();

  return useMutation<SubscriptionSummary, ApiError, void>({
    mutationFn: () => mySubscriptionService.reactivateSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mySubscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
    },
  });
}

export function useMySubscriptionPay() {
  const queryClient = useQueryClient();

  return useMutation<InitiatePaymentResponse, ApiError, { paymentMethod?: string }>({
    mutationFn: ({ paymentMethod }) => mySubscriptionService.initiatePayment(paymentMethod),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mySubscriptionKeys.all });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
    },
  });
}

// Org-scoped mutation hooks — mirror SuperAdmin hooks but use /billing/my-subscription endpoints
// Variable types match the SuperAdmin hooks (orgId is accepted but ignored by the service)

const invalidateMySubscription = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: mySubscriptionKeys.all });
  queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
  queryClient.invalidateQueries({ queryKey: myModulesKeys.all });
  queryClient.invalidateQueries({ queryKey: menuKeys.myMenus() });
};

export function useMyCreateSubscription() {
  const queryClient = useQueryClient();
  return useMutation<SubscriptionSummary, ApiError, { orgId: string; data: CreateSubscriptionRequest }>({
    mutationFn: ({ data }) => mySubscriptionService.createSubscription(data),
    onSuccess: () => invalidateMySubscription(queryClient),
  });
}

export function useMyUpdateSubscription() {
  const queryClient = useQueryClient();
  return useMutation<SubscriptionSummary, ApiError, { orgId: string; data: CreateSubscriptionRequest }>({
    mutationFn: ({ data }) => mySubscriptionService.updateSubscription(data),
    onSuccess: () => invalidateMySubscription(queryClient),
  });
}

export function useMyAddModule() {
  const queryClient = useQueryClient();
  return useMutation<ModuleSubscriptionItem, ApiError, { orgId: string; data: AddModuleRequest }>({
    mutationFn: ({ data }) => mySubscriptionService.addModule(data),
    onSuccess: () => invalidateMySubscription(queryClient),
  });
}

export function useMyChangeTier() {
  const queryClient = useQueryClient();
  return useMutation<ModuleSubscriptionItem, ApiError, { orgId: string; moduleCode: string; data: ChangeTierRequest }>({
    mutationFn: ({ moduleCode, data }) => mySubscriptionService.changeTier(moduleCode, data),
    onSuccess: () => invalidateMySubscription(queryClient),
  });
}

export function useMyUpdateModuleUserCount() {
  const queryClient = useQueryClient();
  return useMutation<ModuleSubscriptionItem, ApiError, { orgId: string; moduleCode: string; userCount: number }>({
    mutationFn: ({ moduleCode, userCount }) => mySubscriptionService.updateUserCount(moduleCode, userCount),
    onSuccess: () => invalidateMySubscription(queryClient),
  });
}

export function useMyRemoveModule() {
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { orgId: string; moduleCode: string; reason?: string }>({
    mutationFn: ({ moduleCode, reason }) => mySubscriptionService.removeModule(moduleCode, reason),
    onSuccess: () => invalidateMySubscription(queryClient),
  });
}

export function useMyConvertTrial() {
  const queryClient = useQueryClient();
  return useMutation<ModuleSubscriptionItem, ApiError, { orgId: string; moduleCode: string }>({
    mutationFn: ({ moduleCode }) => mySubscriptionService.convertTrial(moduleCode),
    onSuccess: () => invalidateMySubscription(queryClient),
  });
}

export function useMyToggleModuleFeature() {
  const queryClient = useQueryClient();
  return useMutation<Record<string, boolean>, ApiError, { orgId: string; moduleCode: string; featureCode: string; enabled: boolean }>({
    mutationFn: ({ moduleCode, featureCode, enabled }) => mySubscriptionService.toggleFeature(moduleCode, featureCode, enabled),
    onSuccess: () => invalidateMySubscription(queryClient),
  });
}
